import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  Collapse,
  FormGroup,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person as PersonIcon,
  Security as SecurityIcon,
  ExpandMore,
  ExpandLess,
  Schedule as ScheduleIcon,
  LockReset,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const MODULE_LABELS = {
  advertising: 'Advertising',
  playlists: 'Playlists',
  clocks: 'Clocks',
  voicetrack: 'Voicetrack',
  live: 'Live',
  library: 'Library',
  ai_content: 'AI Content',
  logger: 'Logger',
  intelligence: 'Intelligence',
  settings: 'Settings',
  studio_output: 'Studio Output',
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (h) => {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

export default function UserManagement() {
  const { user: currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState(null);
  const [emailStatusLoaded, setEmailStatusLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permData, setPermData] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const existingPermRowsRef = useRef([]);

  const [formData, setFormData] = useState({
    accountKind: 'staff',
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    station_id: '',
    password: '',
    role: 'user',
    ai_content_enabled: false,
    logger_enabled: false,
    intelligence_enabled: false,
  });

  useEffect(() => {
    fetchUsers();
    axios.get(`${API_URL}/api/stations`, { withCredentials: true })
      .then((response) => setStations(Array.isArray(response.data) ? response.data : []))
      .catch(() => setStations([]));
    axios.get(`${API_URL}/api/email-settings/status`, { withCredentials: true })
      .then((response) => setEmailDeliveryStatus(response.data))
      .catch(() => setEmailDeliveryStatus(null))
      .finally(() => setEmailStatusLoaded(true));
  }, []);

  const fetchUsers = async () => {
    try {
      const [userResponse, contributorResponse] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/webrtc-contributors`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setUsers(userResponse.data);
      setContributors(Array.isArray(contributorResponse.data) ? contributorResponse.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSaveStatus({ type: 'error', message: 'Failed to load users' });
    }
  };

  const emptyForm = () => ({
    accountKind: 'staff',
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    station_id: '',
    password: '',
    role: 'user',
    ai_content_enabled: false,
    logger_enabled: false,
    intelligence_enabled: false,
  });

  const handleOpenDialog = (account = null) => {
    if (account?.kind === 'contributor') {
      setEditingUser(account);
      setFormData({
        ...emptyForm(),
        accountKind: 'contributor',
        username: account.username || '',
        display_name: account.display_name || '',
        station_id: account.station_id || '',
      });
    } else if (account) {
      const studio = account.account_type === 'studio';
      setEditingUser(account);
      setFormData({
        ...emptyForm(),
        accountKind: studio ? 'studio' : 'staff',
        username: account.username || '',
        email: account.email || '',
        first_name: account.first_name || '',
        last_name: account.last_name || '',
        display_name: account.first_name || '',
        password: '',
        role: account.role || 'user',
        ai_content_enabled: account.ai_content_enabled || false,
        logger_enabled: account.logger_enabled || false,
        intelligence_enabled: account.intelligence_enabled || false,
      });
    } else {
      setEditingUser(null);
      setFormData(emptyForm());
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleSaveUser = async () => {
    if (!formData.username) {
      setSaveStatus({ type: 'error', message: 'Username is required' });
      return;
    }

    setLoading(true);
    setSaveStatus({ type: '', message: '' });

    try {
      if (formData.accountKind === 'contributor') {
        if (!formData.station_id) {
          setSaveStatus({ type: 'error', message: 'Choose the station this contributor sends to' });
          setLoading(false);
          return;
        }
        if ((!editingUser && formData.password.length < 8) || (formData.password && formData.password.length < 8)) {
          setSaveStatus({ type: 'error', message: 'Password must be at least 8 characters' });
          setLoading(false);
          return;
        }
        const body = {
          username: formData.username.trim(),
          display_name: formData.display_name.trim() || null,
          station_id: formData.station_id,
        };
        if (!editingUser) body.is_enabled = true;
        if (formData.password) body.password = formData.password;
        if (editingUser) {
          await axios.put(`${API_URL}/api/webrtc-contributors/${editingUser.id}`, body, { withCredentials: true });
          setSaveStatus({ type: 'success', message: 'Contributor account updated' });
        } else {
          await axios.post(`${API_URL}/api/webrtc-contributors`, body, { withCredentials: true });
          setSaveStatus({ type: 'success', message: 'Contributor account created. Use this username and password in the remote contribution sender.' });
        }
        handleCloseDialog();
        fetchUsers();
        return;
      }

      if (formData.accountKind === 'studio' && !editingUser && formData.password.length < 8) {
        setSaveStatus({ type: 'error', message: 'Password must be at least 8 characters' });
        setLoading(false);
        return;
      }
      if (formData.accountKind === 'studio' && formData.password && formData.password.length < 8) {
        setSaveStatus({ type: 'error', message: 'Password must be at least 8 characters' });
        setLoading(false);
        return;
      }

      const dataToSend = { ...formData };
      delete dataToSend.accountKind;
      delete dataToSend.display_name;
      delete dataToSend.station_id;
      if (formData.accountKind === 'studio') {
        dataToSend.account_type = 'studio';
        dataToSend.role = 'user';
        dataToSend.first_name = formData.display_name.trim() || formData.first_name;
        delete dataToSend.email;
        delete dataToSend.ai_content_enabled;
        delete dataToSend.logger_enabled;
        delete dataToSend.intelligence_enabled;
      }
      // Only send password if provided
      if (!dataToSend.password || dataToSend.password.trim() === '') {
        delete dataToSend.password;
      }
      // Feature entitlements for Admin accounts are controlled by Superadmin only.
      // Do not submit hidden/stale values when an Admin edits or creates accounts.
      if (!isSuperAdmin() || dataToSend.role !== 'admin') {
        delete dataToSend.ai_content_enabled;
        delete dataToSend.logger_enabled;
      }
      if (!isSuperAdmin() || dataToSend.role === 'superadmin') {
        delete dataToSend.intelligence_enabled;
      }

      if (editingUser) {
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, dataToSend, {
          withCredentials: true,
        });
        setSaveStatus({ type: 'success', message: 'User updated successfully' });
        handleCloseDialog();
        fetchUsers();
      } else {
        const response = await axios.post(`${API_URL}/api/users`, dataToSend, {
          withCredentials: true,
        });
        const result = response.data;

        let message = 'User created successfully.';
        if (result.passwordAutoGenerated) {
          message += ' Password was auto-generated.';
        }
        if (result.emailSent) {
          message += ' Login credentials sent to their email.';
        } else if (result.emailError) {
          message += ` Email not sent: ${result.emailError}.`;
        }
        setSaveStatus({
          type: formData.accountKind === 'studio' ? 'success' : (result.emailSent || !result.emailError ? 'success' : 'warning'),
          message: formData.accountKind === 'studio'
            ? 'Studio account created. It signs in on the Studio app with no MFA.'
            : message,
        });

        handleCloseDialog();
        fetchUsers();

        // Auto-open permissions dialog for regular users so admin can set access
        if (result.role === 'user' && result.account_type !== 'studio') {
          setTimeout(() => {
            handleOpenPermDialog(result);
          }, 300);
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to save user',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (targetUser) => {
    if (
      !window.confirm(
        `Reset password for ${targetUser.username}? A new temporary password will be generated and emailed to ${targetUser.email || 'their address'}.`
      )
    ) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/users/${targetUser.id}/reset-password`,
        {},
        { withCredentials: true }
      );
      const result = response.data;
      if (result.emailSent) {
        setSaveStatus({ type: 'success', message: `Password reset. New credentials emailed to ${targetUser.email}.` });
      } else {
        setSaveStatus({
          type: 'warning',
          message: `Password reset but email could not be sent: ${result.emailError || 'SMTP not configured'}.`,
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to reset password',
      });
    }
  };

  const handleDeleteUser = async (account) => {
    const label = account.kind === 'contributor' ? 'contributor account' : (account.account_type === 'studio' ? 'studio account' : 'user');
    if (!window.confirm(`Are you sure you want to delete this ${label}? This action cannot be undone.`)) {
      return;
    }

    try {
      if (account.kind === 'contributor') {
        await axios.delete(`${API_URL}/api/webrtc-contributors/${account.id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API_URL}/api/users/${account.id}`, { withCredentials: true });
      }
      setSaveStatus({ type: 'success', message: 'Account deleted' });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to delete user',
      });
    }
  };

  // ============================================
  // PERMISSION MANAGEMENT
  // ============================================

  const handleOpenPermDialog = async (targetUser) => {
    setPermUser(targetUser);
    try {
      const [permRes, modulesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/${targetUser.id}/permissions`, {
          withCredentials: true,
        }),
        axios.get(`${API_URL}/api/users/meta/modules`, {
          withCredentials: true,
        }),
      ]);
      const existingPerms = permRes.data || [];
      existingPermRowsRef.current = existingPerms;
      // System-feature-filtered list from backend (admins assign these modules)
      const modules = Array.isArray(modulesRes.data) ? modulesRes.data : Object.keys(MODULE_LABELS);
      const perms = modules.map((mod) => {
        const existing = existingPerms.find((p) => p.module === mod);
        return {
          module: mod,
          can_view: existing?.can_view || false,
          can_create: existing?.can_create || false,
          can_edit: existing?.can_edit || false,
          can_delete: existing?.can_delete || false,
          can_manage: existing?.can_manage || false,
          allowed_hours: existing?.allowed_hours ?? null,
          station_id: existing?.station_id ?? null,
          enabled: !!existing,
        };
      });

      setPermData(perms);
      setExpandedModules({});
      setPermDialogOpen(true);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setSaveStatus({ type: 'error', message: 'Failed to load user permissions' });
    }
  };

  const handleToggleModule = (module, enabled) => {
    setPermData((prev) =>
      prev.map((p) =>
        p.module === module
          ? {
              ...p,
              enabled,
              can_view: enabled,
              can_create: enabled,
              can_edit: enabled,
              can_delete: enabled,
              can_manage: enabled,
            }
          : p
      )
    );
  };

  const handlePermChange = (module, field, value) => {
    setPermData((prev) =>
      prev.map((p) => (p.module === module ? { ...p, [field]: value } : p))
    );
  };

  const handleToggleHour = (module, hour) => {
    setPermData((prev) =>
      prev.map((p) => {
        if (p.module !== module) return p;
        const currentHours = p.allowed_hours ?? [];
        const newHours = currentHours.includes(hour)
          ? currentHours.filter((h) => h !== hour)
          : [...currentHours, hour].sort((a, b) => a - b);
        return { ...p, allowed_hours: newHours.length === 24 ? null : newHours };
      })
    );
  };

  const handleSetAllHours = (module) => {
    setPermData((prev) =>
      prev.map((p) => (p.module === module ? { ...p, allowed_hours: null } : p))
    );
  };

  const handleClearAllHours = (module) => {
    setPermData((prev) =>
      prev.map((p) => (p.module === module ? { ...p, allowed_hours: [] } : p))
    );
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;

    setLoading(true);
    setSaveStatus({ type: '', message: '' });

    try {
      const enabledPerms = permData
        .filter((p) => p.enabled)
        .map(({ module, can_view, can_create, can_edit, can_delete, can_manage, allowed_hours, station_id }) => ({
          module,
          can_view,
          can_create,
          can_edit,
          can_delete,
          can_manage,
          allowed_hours,
          station_id,
        }));

      const sameStation = (a, b) => {
        if (a == null && b == null) return true;
        return Number(a) === Number(b);
      };
      const enabledModules = new Set(enabledPerms.map((p) => p.module));
      // Keep other station-scoped rows for modules still enabled in the dialog.
      const preservedSiblings = existingPermRowsRef.current
        .filter((existing) => {
          if (!enabledModules.has(existing.module)) return false;
          const uiRow = enabledPerms.find((p) => p.module === existing.module);
          if (!uiRow) return false;
          return !sameStation(existing.station_id, uiRow.station_id);
        })
        .map(({ module, can_view, can_create, can_edit, can_delete, can_manage, allowed_hours, station_id }) => ({
          module,
          can_view,
          can_create,
          can_edit,
          can_delete,
          can_manage,
          allowed_hours,
          station_id,
        }));

      await axios.put(
        `${API_URL}/api/users/${permUser.id}/permissions`,
        { permissions: [...enabledPerms, ...preservedSiblings] },
        { withCredentials: true }
      );

      setSaveStatus({ type: 'success', message: 'Permissions saved successfully' });
      setPermDialogOpen(false);
      setPermUser(null);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to save permissions',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandModule = (module) => {
    setExpandedModules((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return { bg: '#ff6b6b30', color: '#ff6b6b' };
      case 'admin':
        return { bg: '#22d3ee30', color: '#22d3ee' };
      case 'studio':
        return { bg: '#60a5fa30', color: '#60a5fa' };
      case 'contributor':
        return { bg: '#f59e0b30', color: '#f59e0b' };
      default:
        return { bg: '#66666630', color: '#999' };
    }
  };

  const accountLabel = (account) => {
    if (account.kind === 'contributor') return 'Contributor';
    if (account.account_type === 'studio') return 'Studio';
    return account.role;
  };

  const canManageUser = (targetUser) => {
    if (targetUser.kind === 'contributor') return isSuperAdmin() || isAdmin();
    if (targetUser.id === currentUser?.id && targetUser.kind !== 'contributor') return false;
    if (isSuperAdmin()) return true;
    // Admin can manage regular users and other admins (not superadmins)
    if (isAdmin()) return targetUser.role === 'user' || targetUser.role === 'admin';
    return false;
  };

  const canResetPassword = (targetUser) => {
    if (targetUser.id === currentUser?.id) return false;
    if (isSuperAdmin()) return true;
    // Admin can reset passwords for regular users and other admins (not superadmins)
    if (isAdmin()) return targetUser.role === 'user' || targetUser.role === 'admin';
    return false;
  };

  const accounts = [
    ...users.map((user) => ({
      ...user,
      kind: user.account_type === 'studio' ? 'studio' : 'staff',
      listKey: `staff-${user.id}`,
    })),
    ...contributors.map((contributor) => ({
      ...contributor,
      kind: 'contributor',
      role: 'contributor',
      listKey: `contributor-${contributor.id}`,
    })),
  ].sort((a, b) => String(a.username).localeCompare(String(b.username)));

  const handleToggleMfaRequired = async (userId, required) => {
    try {
      await axios.put(
        `${API_URL}/api/mfa/admin/${userId}/require`,
        { required },
        { withCredentials: true }
      );
      setSaveStatus({
        type: 'success',
        message: `MFA ${required ? 'required' : 'optional'} for user`,
      });
      fetchUsers();
    } catch (error) {
      console.error('Error toggling MFA requirement:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to update MFA requirement' });
    }
  };

  const handleResetMfa = async (targetUser) => {
    if (!window.confirm(`Reset MFA for ${targetUser.username}? They will need to set it up again.`)) {
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/mfa/admin/${targetUser.id}/reset`,
        {},
        { withCredentials: true }
      );
      setSaveStatus({ type: 'success', message: response.data.message });
      fetchUsers();
    } catch (error) {
      console.error('Error resetting MFA:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.message || 'Failed to reset MFA' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#22d3ee' }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
        >
          New account
        </Button>
      </Box>

      {emailStatusLoaded && emailDeliveryStatus?.ready && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Email is configured. Login credentials and password resets will be sent when a user has an address.
        </Alert>
      )}
      {emailStatusLoaded && !emailDeliveryStatus?.ready && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {emailDeliveryStatus?.configured
            ? 'SMTP is saved, but email notifications are off, so credentials will not be emailed.'
            : 'SMTP is not configured, so credentials will not be emailed.'}
          {isSuperAdmin()
            ? ' Open Email / SMTP in Settings to configure it and confirm the connection works.'
            : ' Ask a superadmin to configure Email / SMTP in Settings.'}
        </Alert>
      )}

      {saveStatus.message && (
        <Alert
          severity={saveStatus.type}
          sx={{ mb: 3 }}
          onClose={() => setSaveStatus({ type: '', message: '' })}
        >
          {saveStatus.message}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No users found.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-paper)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#22d3ee' }}>User</TableCell>
                <TableCell sx={{ color: '#22d3ee' }}>Role</TableCell>
                <TableCell sx={{ color: '#22d3ee' }}>Email</TableCell>
                <TableCell sx={{ color: '#22d3ee' }}>MFA</TableCell>
                <TableCell sx={{ color: '#22d3ee' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((u) => {
                const roleColors = getRoleColor(accountLabel(u).toLowerCase());
                const isCurrentUser = u.kind !== 'contributor' && u.id === currentUser?.id;
                const isServiceAccount = u.kind === 'contributor' || u.account_type === 'studio';
                const stationName = stations.find((station) => String(station.id) === String(u.station_id))?.name;
                return (
                  <TableRow key={u.listKey} sx={{ opacity: isCurrentUser ? 0.7 : 1 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: roleColors.color, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2">
                            {u.username}
                            {isCurrentUser && (
                              <Chip label="you" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                            )}
                          </Typography>
                          {(u.display_name || u.first_name || u.last_name || stationName) && (
                            <Typography variant="caption" color="text.secondary">
                              {u.kind === 'contributor' ? (u.display_name || stationName || '') : `${u.first_name || ''} ${u.last_name || ''}`.trim()}
                              {u.kind === 'contributor' && stationName ? ` · ${stationName}` : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={accountLabel(u)}
                        size="small"
                        sx={{
                          bgcolor: roleColors.bg,
                          color: roleColors.color,
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {u.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isServiceAccount ? (
                        <Chip
                          label="No MFA"
                          size="small"
                          sx={{ bgcolor: 'var(--rs-border)80', color: '#9aa7b8', fontSize: '0.7rem', height: 22 }}
                        />
                      ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {u.mfa_enabled ? (
                          <Chip
                            label="Enabled"
                            size="small"
                            sx={{ bgcolor: '#4caf5030', color: '#4caf50', fontSize: '0.7rem', height: 22 }}
                          />
                        ) : (
                          <Chip
                            label="Off"
                            size="small"
                            sx={{ bgcolor: 'var(--rs-border)80', color: '#666', fontSize: '0.7rem', height: 22 }}
                          />
                        )}
                        {canManageUser(u) && (
                          <Tooltip title={u.mfa_required ? 'MFA required (click to make optional)' : 'Require MFA'}>
                            <Switch
                              checked={u.mfa_required || false}
                              onChange={(e) => handleToggleMfaRequired(u.id, e.target.checked)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {canManageUser(u) && (
                          <>
                            <Tooltip title="Edit User">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(u)}
                                sx={{ color: '#22d3ee' }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {u.kind !== 'contributor' && u.role === 'user' && (
                              <Tooltip title="Permissions">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenPermDialog(u)}
                                  sx={{ color: '#4caf50' }}
                                >
                                  <SecurityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!isServiceAccount && canResetPassword(u) && (
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetPassword(u)}
                                  sx={{ color: '#ff9800' }}
                                >
                                  <LockReset fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canManageUser(u) && u.mfa_enabled && (
                              <Tooltip title="Reset MFA">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetMfa(u)}
                                  sx={{ color: '#9c27b0' }}
                                >
                                  <SecurityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete User">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(u)}
                                sx={{ color: '#ff6b6b' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {isCurrentUser && (
                          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            Current session
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#22d3ee' }} />
            <Typography variant="h6">
              {editingUser
                ? (formData.accountKind === 'studio' ? 'Edit studio account' : formData.accountKind === 'contributor' ? 'Edit contributor account' : 'Edit user')
                : 'Create account'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username *"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                disabled={!!editingUser}
              />
            </Grid>
            {!editingUser && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Account</InputLabel>
                  <Select
                    value={formData.accountKind}
                    label="Account"
                    onChange={(e) => setFormData({ ...formData, accountKind: e.target.value })}
                  >
                    <MenuItem value="staff">Staff user</MenuItem>
                    <MenuItem value="studio">Studio</MenuItem>
                    <MenuItem value="contributor">Contributor</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {formData.accountKind === 'studio'
                    ? 'Username and password for the RadioSuite Studio app. MFA is not used.'
                    : formData.accountKind === 'contributor'
                      ? 'Username and password for a remote contribution sender, such as satellite news. MFA is not used. Put this username on a WebRTC Receiver clock item.'
                      : 'A normal RadioSuite user for the website.'}
                </Typography>
              </Grid>
            )}
            {formData.accountKind === 'staff' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Grid>
            )}
            {formData.accountKind === 'staff' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Grid>
            )}
            {formData.accountKind === 'staff' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                helperText={
                  !editingUser
                    ? (emailDeliveryStatus?.ready
                      ? 'If provided, login credentials will be emailed to this address'
                      : (emailStatusLoaded
                        ? 'SMTP is not ready, so credentials will not be emailed even if an address is provided'
                        : 'If provided, login credentials will be emailed when SMTP is configured'))
                    : ''
                }
              />
            </Grid>
            )}
            {formData.accountKind !== 'staff' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                />
              </Grid>
            )}
            {formData.accountKind === 'contributor' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={formData.station_id}
                    label="Station"
                    onChange={(e) => setFormData({ ...formData, station_id: e.target.value })}
                  >
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={formData.accountKind === 'staff'
                  ? (editingUser ? 'New Password (leave blank to keep current)' : 'Password (leave blank to auto-generate)')
                  : (editingUser ? 'New password (leave blank to keep current)' : 'Password')}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                helperText={formData.accountKind === 'staff'
                  ? (editingUser
                    ? 'Setting a new password will force the user to change it on next login'
                    : (emailDeliveryStatus?.ready
                      ? 'If left blank, a secure password will be auto-generated and emailed to the user'
                      : 'If left blank, a secure password will be auto-generated'))
                  : 'At least 8 characters. This password is used as-is, with no MFA.'}
              />
            </Grid>
            {formData.accountKind === 'staff' && (isSuperAdmin() || isAdmin()) && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {isSuperAdmin() && <MenuItem value="superadmin">Super Admin</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.accountKind === 'staff' && isSuperAdmin() && formData.role === 'admin' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.ai_content_enabled}
                      onChange={(e) =>
                        setFormData({ ...formData, ai_content_enabled: e.target.checked })
                      }
                    />
                  }
                  label="Enable AI Content Access"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7 }}>
                  When enabled, this admin can access AI Content, AI Features settings, and Banned Words.
                </Typography>
              </Grid>
            )}
            {formData.accountKind === 'staff' && isSuperAdmin() && formData.role === 'admin' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.logger_enabled}
                      onChange={(e) =>
                        setFormData({ ...formData, logger_enabled: e.target.checked })
                      }
                    />
                  }
                  label="Enable Logger Access"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7 }}>
                  When enabled, this admin can access the Stream Logger to browse and manage recordings.
                </Typography>
              </Grid>
            )}
            {formData.accountKind === 'staff' && isSuperAdmin() && formData.role !== 'superadmin' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.intelligence_enabled}
                      onChange={(e) =>
                        setFormData({ ...formData, intelligence_enabled: e.target.checked })
                      }
                    />
                  }
                  label="Enable Intelligence Access"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7 }}>
                  When enabled, this person can run Intelligence. The installation also needs AI Content turned on and a Grok API key.
                </Typography>
              </Grid>
            )}
            {editingUser && formData.accountKind === 'staff' && editingUser.role === 'user' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  onClick={() => {
                    handleCloseDialog();
                    setTimeout(() => handleOpenPermDialog(editingUser), 200);
                  }}
                  sx={{
                    mt: 1,
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    '&:hover': { borderColor: '#66bb6a', bgcolor: 'rgba(76,175,80,0.08)' },
                  }}
                >
                  Permissions
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Open the full module checklist to grant or revoke access (including Studio Output when enabled).
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            sx={{ bgcolor: '#22d3ee' }}
            disabled={loading}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog
        open={permDialogOpen}
        onClose={() => setPermDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#4caf50' }} />
            <Typography variant="h6">
              Permissions for {permUser?.username}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Enable modules to grant access. For Voicetracks, Playlists, and Clocks (schedule logs), you can
              optionally restrict access to specific hours of the day.
            </Typography>
          </Alert>

          {permData.map((perm) => {
            const isExpanded = expandedModules[perm.module];
            const hasHourRestriction = perm.module === 'voicetrack' || perm.module === 'playlists' || perm.module === 'clocks';
            const hourRestrictionActive = hasHourRestriction && perm.allowed_hours !== null;

            return (
              <Card
                key={perm.module}
                sx={{
                  mb: 1,
                  bgcolor: perm.enabled ? '#1a2a1a' : 'var(--rs-paper)',
                  border: `1px solid ${perm.enabled ? '#4caf5040' : 'var(--rs-border)'}`,
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Switch
                        checked={perm.enabled}
                        onChange={(e) => handleToggleModule(perm.module, e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#4caf50',
                          },
                        }}
                      />
                      <Typography variant="body1" sx={{ fontWeight: perm.enabled ? 500 : 400 }}>
                        {MODULE_LABELS[perm.module] || perm.module}
                      </Typography>
                      {hourRestrictionActive && (
                        <Chip
                          label={`${(perm.allowed_hours || []).length} hours`}
                          size="small"
                          sx={{ bgcolor: '#ff980020', color: '#ff9800', fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                    {perm.enabled && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasHourRestriction && (
                          <Tooltip title="Configure hour restrictions">
                            <IconButton
                              size="small"
                              onClick={() => toggleExpandModule(perm.module)}
                              sx={{ color: '#ff9800' }}
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => toggleExpandModule(perm.module)}
                          sx={{ color: '#666' }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Collapse in={isExpanded && perm.enabled}>
                    <Box sx={{ mt: 2, pl: 5 }}>
                      {/* Fine-grained permissions */}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Permissions
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={perm.can_view}
                              onChange={(e) =>
                                handlePermChange(perm.module, 'can_view', e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">View</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={perm.can_create}
                              onChange={(e) =>
                                handlePermChange(perm.module, 'can_create', e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Create</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={perm.can_edit}
                              onChange={(e) =>
                                handlePermChange(perm.module, 'can_edit', e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Edit</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={perm.can_delete}
                              onChange={(e) =>
                                handlePermChange(perm.module, 'can_delete', e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Delete</Typography>}
                        />
                      </FormGroup>

                      {perm.module === 'live' && (
                        <FormControl fullWidth size="small" sx={{ mt: 2, maxWidth: 360 }}>
                          <InputLabel>Live station scope</InputLabel>
                          <Select
                            label="Live station scope"
                            value={perm.station_id ?? ''}
                            onChange={(event) =>
                              handlePermChange(
                                perm.module,
                                'station_id',
                                event.target.value === '' ? null : event.target.value
                              )
                            }
                          >
                            <MenuItem value="">All stations</MenuItem>
                            {stations.map((station) => (
                              <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {/* Hour Restrictions */}
                      {hasHourRestriction && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ScheduleIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                              <Typography variant="caption" color="text.secondary">
                                Hour Restrictions
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                onClick={() => handleSetAllHours(perm.module)}
                                sx={{ fontSize: '0.7rem', color: '#4caf50' }}
                              >
                                All Hours
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleClearAllHours(perm.module)}
                                sx={{ fontSize: '0.7rem', color: '#ff6b6b' }}
                              >
                                Clear
                              </Button>
                            </Box>
                          </Box>

                          {perm.allowed_hours === null ? (
                            <Alert severity="success" sx={{ py: 0, mb: 1 }}>
                              <Typography variant="caption">
                                No hour restrictions - user can access all hours. Click individual hours below to restrict.
                              </Typography>
                            </Alert>
                          ) : (
                            <Alert severity="warning" sx={{ py: 0, mb: 1 }}>
                              <Typography variant="caption">
                                Restricted to {(perm.allowed_hours || []).length} of 24 hours.
                              </Typography>
                            </Alert>
                          )}

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {HOURS.map((hour) => {
                              const isAllowed =
                                perm.allowed_hours === null || (perm.allowed_hours || []).includes(hour);
                              return (
                                <Chip
                                  key={hour}
                                  label={formatHour(hour)}
                                  size="small"
                                  onClick={() => {
                                    if (perm.allowed_hours === null) {
                                      // Switching from "all hours" to restricted: enable all but this one
                                      const allButThis = HOURS.filter((h) => h !== hour);
                                      handlePermChange(perm.module, 'allowed_hours', allButThis);
                                    } else {
                                      handleToggleHour(perm.module, hour);
                                    }
                                  }}
                                  sx={{
                                    bgcolor: isAllowed ? '#4caf5030' : 'var(--rs-border)80',
                                    color: isAllowed ? '#4caf50' : '#666',
                                    border: `1px solid ${isAllowed ? '#4caf5050' : '#3a445650'}`,
                                    fontSize: '0.65rem',
                                    height: 24,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: isAllowed ? '#4caf5050' : '#3a445680',
                                    },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            disabled={loading}
          >
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
